import * as Actions from 'actions';
import * as ActionTypes from 'constants';
import { delay } from 'redux-saga';
import { select, call, put, take, race, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { startSockets, sockets, on, emit } from 'utils';
import { storage } from 'storage';
import config from 'config';
import * as Api from 'api';

/***************************** Selectors ************************************/
export const getUsers = state => (state.users);

/***************************** Helpers ************************************/

storage.getItem('token').then(token => {
  Api.default({ token, url: config.url, url_v4: config.url_v4, version: 4 });
})

/***************************** Subroutines ************************************/

function * initSession() {
  while(true) {
    try {
      yield call(checkAuthorization);
      const result = yield race({
        response : call(delay, 10 * 60 * 1000),
        cancel : take(ActionTypes.LOGOUT),
        retry : take(ActionTypes.CHECK_AUTHORIZATION),
      });
      if(result.cancel) {
        yield call(logout);
      }
    }
    catch(e) {
      try {
        console.log('Error thrown in checking authorization: ', e)
        Api.setAuthToken('');
        yield storage.removeItem('token');
        yield storage.removeItem('username');

        yield put(Actions.userLoggedOut());
        let {payload : creds, callback} = yield take(ActionTypes.LOGIN);
        console.log('Login occured.')
        let status = yield call(login, creds);
        if(callback) {
          callback(null, status);
        }
      	let token = yield storage.getItem('token');
        if(!token) {
          throw new Error('No session found. Please try again.')
        }
        yield call(loginSockets, token);


      }
      catch(e) {
        yield put(Actions.notify({ message : e.message }));
        yield put({ type : ActionTypes.LOGIN_FAILED, payload : { message : e.message }});
        yield put(Actions.userLoggedOut());
      }
    }
  }
}

function * checkAuthorization() {
  let token = yield storage.getItem('token');
  let username = yield storage.getItem('username');

  if(!username || !token) {
    throw Error('You are not logged in.');
  }

  yield call(Api.checkAuthorization, username, token);
  yield put(Actions.updateToken(token));

  const user = yield call(Api.fetchCurrentUser);
  yield put(Actions.updateUser(user));
  yield put(Actions.userLoggedIn());
  yield put(Actions.joinSocketChannel(`account-${user.owner_account || user._id}`));
  yield put(Actions.joinSocketChannel(`user-${user._id}`));
}

function * login(creds) {
  let {
  	token : newToken,
  	validating
  } = yield call(Api.login, creds.username, creds.password);
  if(validating) {
  	yield put(Actions.updateTmpUser({username : creds.username, password : creds.password}))
  	yield put(Actions.push('/validate-account'))
  	yield Promise.resolve(null)
  }
  else {
  	yield storage.setItem('username', creds.username, {path : '/'});
	  yield storage.setItem('token', newToken, {path : '/'});

    Api.setAuthToken(newToken);
	  yield Promise.resolve(newToken);
  }
}

function * logout() {
  console.log('Started logout...')
  let token = yield storage.getItem('token');
  let username = yield storage.getItem('username');

  if(token || username) {
    yield call(Api.logout, username, token);
    yield call(logoutSockets);
    yield storage.removeItem('token');
    yield storage.removeItem('username');
    yield put(Actions.updateToken(''));
    console.log('Done emptying login state...');
  }
}

function * validateAccount(action) {
	try {
		let users = yield select(getUsers);
		let username = users.tmp_user ? users.tmp_user.username : ''
		let status = yield call(Api.validateAccount, action.payload, username)
		if(status.valid) {
			if(!users.tmp_user || !users.tmp_user.username || !users.tmp_user.password) {
				throw new Error('Invalid login attempt')
			}
			else {
				yield put(Actions.login({
					username : users.tmp_user.username,
					password : users.tmp_user.password
				}))
				yield put(Actions.push('/'))
			}
		}
	}
	catch(ex) {
		yield put(Actions.notify({message : ex.message || ex}))
		yield put(Actions.push('/'))
	}
}

/******************************************************************************/
/******************************* SOCKET INIT *************************************/
/******************************************************************************/

const getChannels = ({socket}) => (socket.channels);

function * initSockets() {
  try {
    startSockets();
  }
  catch(ex) {
    console.error(ex);
  }
  yield all([
    call(subscribe),
    call(connect),
    call(disconnect),
    call(message),
    call(unsubscribe),
  ])
}

function * loginSockets( token ) {
  console.log('logging in sockets!')
  const response = yield emit('login', token);
  console.log('login sockets success!' , response)
  const channels = yield select(getChannels);
  channels.forEach( channel => {
    sockets().subscribe(channel);
  } );
}

function * logoutSockets() {
  const channels = yield select(getChannels);
  channels.forEach( channel => {
    sockets().unsubscribe(channel);
  } );
}

function * subscribe() {
  while(true) {
    const data = yield call(on, 'subscribe');
    yield put({ type : ActionTypes.JOIN_SOCKET_CHANNEL_SUCCESS });
  }
}
function * unsubscribe() {
  while(true) {
    const data = yield call(on, 'unsubscribe');
    yield put({ type : ActionTypes.LEAVE_SOCKET_CHANNEL_SUCCESS });
  }
}
function * message() {
  while(true) {

    const msgData = yield call(on, 'message')
    if(msgData === '#1') {
      continue;
    }

    const msg = JSON.parse(msgData);
    if(!msg || !msg.event || !msg.data || !msg.data.channel) {
      continue;
    }

    let { channel, data : payload } = msg.data;

    if(typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      }
      catch(ex) {
        //Do nothing.
      }
    }

    const { event } = payload;

    yield put({ type : event, payload });
  }
}

function * connect() {
  while(true) {
    try {
      yield call(on, 'connect');
      yield put(Actions.setSocketsConnected());
      console.log('Connecting...');
      const token = yield storage.getItem('token');
      if(!token) {
        throw Error('No token exists.')
      }
      yield call(loginSockets, token);
    }
    catch(e) {
      console.log('Error with socket login: ', e)
      yield put({ type : ActionTypes.LOGIN_FAILED, message : e.message });
    }
  }
}

function * disconnect() {
  while(true) {
    const error = yield call(on, 'disconnect');
    yield put(Actions.setSocketsDisconnected());

  }
}

function* joinChannel(action) {
  try {
    sockets().subscribe(action.payload.channel);
  } catch (e) {
    yield put({ type: ActionTypes.JOIN_SOCKET_CHANNEL_FAILED, payload: {error : e, channel : action.payload.channel} });
  }
}

function* leaveChannel(action) {
  try {
    sockets().unsubscribe(action.payload.channel);
  } catch (e) {
    yield put({ type: 'FAILURE' });
  }
}

function * performEmit(action) {
  try {
    const success = yield emit(action.payload.event, action.payload);
    if(action.payload.cb) {
      action.payload.cb(null, success);
    }
  }
  catch(ex) {
    if(action.payload.cb) {
      action.payload.cb(ex, null);
    }
    const nextAction = `${action.payload.event.replace('-', '_').replace('-','_').toUpperCase()}_FAILED`;
    const { message } = ex;
    yield put({ type : nextAction, payload : { message, error: ex } });
  }
}



/******************************************************************************/
/******************************* WATCHERS *************************************/
/******************************************************************************/
function * init() {
	yield put(Actions.toggleInitFlag(true))
  yield all([
    call(initSession),
    call(initSockets),
  ])
  yield put(Actions.toggleInitFlag(false))
}

export function * sessionWatcher() {
  yield all([
    takeEvery(ActionTypes.INIT, init),
    // takeEvery(ActionTypes.LOGOUT, logout),
    takeEvery(ActionTypes.JOIN_SOCKET_CHANNEL, joinChannel),
    takeEvery(ActionTypes.LEAVE_SOCKET_CHANNEL, leaveChannel),
    takeEvery(ActionTypes.SEND_SOCKET_EVENT, performEmit),
    takeEvery(ActionTypes.BEGIN_VALIDATE_ACCOUNT, validateAccount)
  ])
}



// WEBPACK FOOTER //
// ./src/common/sagas/session-authorization.js